import React, { useEffect, useState } from "react";
import { Modal, Form, Upload, Row, Col, Input, notification } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import config from "../../../../config/config";
import helper from "../../../../helpers/helper";

const Edit = ({
  isVisible,
  handleClose,
  moduleName,
  base_url,
  record,
  azure_base,
  recordIndex,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});

  const sizeLimit = config.sizeLimit;
  const allowedExtensions = config.allowed_extensions;

  const validate = (values) => {
    const errors = {};

    ["webImg", "mobileImg", "iconImg"].forEach((field) => {
      if (values[field]?.[0]?.originFileObj) {
        helper.validateSize(
          field,
          values[field][0].originFileObj,
          sizeLimit,
          errors
        );
        helper.validateExt(
          field,
          values[field][0].originFileObj,
          allowedExtensions,
          errors
        );
      }
    });

    if (!values.title?.trim()) errors.title = "Title is required";

    return errors;
  };

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        title: record.title,
        webImg: record.webImg
          ? [
              {
                uid: "-1",
                name: "web.png",
                status: "done",
                url: azure_base + record.webImg, // <-- full path for preview
              },
            ]
          : [],
        mobileImg: record.mobileImg
          ? [
              {
                uid: "-2",
                name: "mobile.png",
                status: "done",
                url: azure_base + record.mobileImg,
              },
            ]
          : [],
        iconImg: record.iconImg
          ? [
              {
                uid: "-3",
                name: "icon.png",
                status: "done",
                url: azure_base + record.iconImg,
              },
            ]
          : [],
      });
    }
  }, [record, form, azure_base]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields(); // get all form values
      setFormError({});

      setLoading(true);
      const formData = new FormData();

      // Always send the current benefit info
      const itemsArray = [
        {
          _id: record._id,
          title: values.title,
          webImg: record.webImg,
          mobileImg: record.mobileImg,
          iconImg: record.iconImg,
        },
      ];

      formData.append("items", JSON.stringify(itemsArray));

      // Append new files if the user replaced them
      if (values.webImg?.[0]?.originFileObj) {
        formData.append("webImg_0", values.webImg[0].originFileObj);
      }
      if (values.mobileImg?.[0]?.originFileObj) {
        formData.append("mobileImg_0", values.mobileImg[0].originFileObj);
      }
      if (values.iconImg?.[0]?.originFileObj) {
        formData.append("iconImg_0", values.iconImg[0].originFileObj);
      }

      // Call backend with query params
      const res = await axios.post(
        `${base_url}updateBenefits?parentId=${record.parentId}&benefitId=${record._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      notification.success({
        message: res.data.message || `${moduleName} updated successfully`,
      });
      handleClose(true);
    } catch (err) {
      console.error(err);
      notification.error({
        message: err?.response?.data?.message || "Update failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    beforeUpload: (file) => false, // prevent auto upload
    onRemove: (file) => {
      // remove file from Form.Item value manually
      const currentFiles =
        form.getFieldValue(
          file.uid.includes("web")
            ? "webImg"
            : file.uid.includes("mobile")
            ? "mobileImg"
            : "iconImg"
        ) || [];
      form.setFieldsValue({
        [file.uid.includes("web")
          ? "webImg"
          : file.uid.includes("mobile")
          ? "mobileImg"
          : "iconImg"]: currentFiles.filter((f) => f.uid !== file.uid),
      });
    },
    maxCount: 1,
    listType: "picture-card",
    showUploadList: { showPreviewIcon: false, showRemoveIcon: true },
  };

  const recommendedSizes = [
    "HEALTH COVERAGE - </br> Desktop - 778 × 440",
    "RETIREMENT BENEFITS -</br>  Desktop - 400 × 440",
    "PAID TIME OFF (PTO) -</br>  Desktop - 400 × 440",
    "FLEX WORK SCHEDULE - Desktop - 1020 × 440",
    "CAREER DEVELOPMENT OPPORTUNITIES - Desktop - </br> 585 × 440",
  ];

  return (
    <Modal
      title={`Edit ${moduleName}`}
      open={isVisible}
      onCancel={() => handleClose(false)}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={loading}
      width={700}
      onOk={() =>
        form
          .validateFields()
          .then(handleSubmit)
          .catch(() => {})
      }
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Desktop Image"
              name="webImg"
              extra={
                <span
                  dangerouslySetInnerHTML={{
                    __html: recommendedSizes[recordIndex] || "N/A",
                  }}
                />
              }
              valuePropName="fileList"
              getValueFromEvent={(e) =>
                Array.isArray(e) ? e : e?.fileList || []
              }
              {...(formError.webImg && {
                help: formError.webImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label="Mobile Image"
              name="mobileImg"
              extra="Mobile - 345 × 225"
              valuePropName="fileList"
              getValueFromEvent={(e) =>
                Array.isArray(e) ? e : e?.fileList || []
              }
              {...(formError.mobileImg && {
                help: formError.mobileImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label="Icon"
              name="iconImg"
              extra="Logo size - 68 × 68"
              valuePropName="fileList"
              getValueFromEvent={(e) =>
                Array.isArray(e) ? e : e?.fileList || []
              }
              {...(formError.iconImg && {
                help: formError.iconImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="Title"
              name="title"
              {...(formError.title && {
                help: formError.title,
                validateStatus: "error",
              })}
            >
              <Input placeholder="Enter Title" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Edit;
